import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CopyField } from "./CopyField";
import { ResetPasswordButton } from "./ResetPasswordButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const ROLE_LABEL = {
  company_admin: "Admin — full access including billing and users",
  company_user: "Team member — customers, applications and archive",
};

export const TeamUsersTab = ({ isAdmin, onUpgrade }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "company_user" });

  const load = useCallback(async () => {
    const res = await api.get("/company/users");
    setData(res.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: created } = await api.post("/company/users", {
        ...form,
        origin_url: window.location.origin,
      });
      toast.success(
        created.sent
          ? `${created.name} invited — setup email sent to ${created.email}`
          : `${created.name} added, but the email failed: ${created.error}. Password: ${created.temp_password}`
      );
      setOpen(false);
      setForm({ name: "", email: "", role: "company_user" });
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? Their login stops working immediately.`)) return;
    try {
      await api.delete(`/company/users/${u.id}`);
      toast.success(`${u.email} removed`);
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!data) return null;
  const full = data.used >= data.limit;

  return (
    <div className="space-y-6" data-testid="team-users-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-xl">
            <h2 className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight text-slate-900">
              <Users className="h-4 w-4 text-blue-600" /> User accounts
            </h2>
            <p className="mt-1.5 text-sm text-slate-500" data-testid="team-users-summary">
              {data.used} of {data.limit} user account{data.limit === 1 ? "" : "s"} used on your{" "}
              {data.plan_name} plan. Each person gets their own login and chooses their own
              password from the emailed setup link.
            </p>
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  disabled={full}
                  data-testid="add-team-user-button"
                  className="h-11 rounded-full bg-blue-600 px-6 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95 disabled:opacity-60"
                >
                  <UserPlus className="mr-2 h-4 w-4" /> Invite a user
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
                    Invite someone to your workspace
                  </DialogTitle>
                  <DialogDescription className="text-sm text-slate-500">
                    They receive an email with a setup link and choose their own password.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5" data-testid="team-user-form">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Full name
                    </Label>
                    <Input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="team-user-name-input"
                      className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Work email
                    </Label>
                    <Input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="team-user-email-input"
                      className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Access level
                    </Label>
                    {Object.entries(ROLE_LABEL).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setForm({ ...form, role: value })}
                        data-testid={`team-user-role-${value}`}
                        className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors duration-200 ${
                          form.role === value
                            ? "border-blue-600 bg-blue-50/50"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <ShieldCheck
                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                            form.role === value ? "text-blue-600" : "text-slate-400"
                          }`}
                        />
                        <span className="text-sm text-slate-700">{label}</span>
                      </button>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={busy}
                      data-testid="team-user-submit-button"
                      className="h-11 w-full rounded-md bg-blue-600 font-semibold hover:bg-blue-700"
                    >
                      {busy ? "Inviting…" : "Send the invite"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {full && isAdmin && (
          <div
            className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 p-5"
            data-testid="team-users-full-note"
          >
            <p className="text-sm font-medium text-amber-900">
              Every user account on your {data.plan_name} plan is in use. Small Business ($49/month
              plus tax when applicable) includes up to 3 users and Professional ($99/month plus tax
              when applicable) includes up to 10.
            </p>
            <Button
              onClick={onUpgrade}
              data-testid="team-users-upgrade-button"
              className="rounded-full bg-blue-600 px-6 font-semibold hover:bg-blue-700"
            >
              See plans
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="divide-y divide-slate-100" data-testid="team-users-list">
          {data.users.map((u) => (
            <div
              key={u.id}
              data-testid={`team-user-row-${u.id}`}
              className="px-6 py-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="font-display text-base font-bold text-slate-900">
                    {u.name || u.email}
                    {u.is_you && (
                      <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                        you
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{u.email}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      u.role === "company_admin"
                        ? "bg-blue-50 text-blue-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                    data-testid={`team-user-role-badge-${u.id}`}
                  >
                    {u.role === "company_admin" ? "Admin" : "Team member"}
                  </span>
                  {isAdmin && (
                    <ResetPasswordButton
                      testId={`team-user-reset-${u.id}`}
                      label={u.email}
                      endpoint={`/company/users/${u.id}/reset-password`}
                      onDone={load}
                    />
                  )}
                  {isAdmin && !u.is_you && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => remove(u)}
                      data-testid={`team-user-remove-${u.id}`}
                      className="rounded-full border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50"
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              {u.temp_password && u.setup_link && (
                <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                  <CopyField
                    label="Setup link"
                    value={`${window.location.origin}${u.setup_link}`}
                    testId={`team-user-setup-${u.id}`}
                  />
                  <CopyField
                    label="Backup password"
                    value={u.temp_password}
                    testId={`team-user-password-${u.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
